import Center from "@/Components/Base/Center";
import Button from "@/Components/Button/Button";
import Icon from "@/Components/Icon/Icon";
import Modal, { ModalProps } from "@/Components/Modal/Modal";
import { QwertyKeyboard } from "@/Components/NumbersKeyPad/Keyboard";
import { Numpad } from "@/Components/NumbersKeyPad/NumbersKeyPad";
import { useModalMeta, useSetModal } from "@/shared/atoms/useModal";
import usePriorityModal, {
  useSetPriorityModal,
} from "@/shared/atoms/usePriorityModal";
import useBranchId from "@/shared/hooks/useBranchId";
import { useCart, useCartTotal } from "@/shared/hooks/useCartHook";
import useCartValidatorHook from "@/shared/hooks/useCartValidatorHook";
import useCartValidatorValuesHook from "@/shared/hooks/useCartValidatorValuesHook";
import useCommon from "@/shared/hooks/useCommon";
import useDiscountCodeHook from "@/shared/hooks/useDiscountCodeHook";
import usePrintReceipt from "@/shared/hooks/usePrintReceipt";
import usePosOrderPost from "@/shared/mutations/pos/usePosOrderPost";
import useKioskSettingsQuery from "@/shared/queries/kioskSettings/useKioskSettingsQuery";
import usePosUserSettingsQuery from "@/shared/queries/pos/usePosUserSettingsQuery";
import { cn } from "@/shared/utils/cn";
import {
  PaymentMethodEnum,
  PaymentStatusEnum,
  Sources,
} from "@/shared/utils/enum";
import { formatPrice } from "@/shared/utils/helpers";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import useOrderQuery from "@/shared/v2/queries/useOrderQuery";
import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";
import KioskCartItem from "../Kiosk/KioskCartItem";
import KioskProduct from "../Kiosk/KioskProduct";
import useKioskCategory from "../Kiosk/useKioskCategory";
import useKioskReset from "./useKioskReset";

export interface TableModalProps extends ModalProps {}

const KioskPayments = ({ paymentMethod, setPaymentMethod }) => {
  const t = useCommon();

  return (
    <div className="flex h-full flex-col">
      <div
        className="shrink-0 text-center text-[5rem]"
        style={{ fontWeight: "900" }}
      >
        {t("howDoYouWantPay")}
      </div>
      <div className="kiosk-scroll-visible flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain">
        <div className="cursor-pointer my-auto py-16">
          <Row justify="center">
            <Col xs={6}>
              <Center
                className={cn(
                  paymentMethod === 22
                    ? "bg-primary text-white"
                    : "bg-grey text-black",
                  "flex flex-col p-8 gap-4 rounded-lg"
                )}
                onClick={() => setPaymentMethod(22)}
              >
                <Icon
                  variants="creditcard"
                  className={`h-24 w-24 ${
                    paymentMethod === 22
                      ? "fill-white text-white"
                      : "fill-dark text-dark"
                  }`}
                />
                <div>{t("payWithCard")}</div>
              </Center>
            </Col>
            {/* <Col xs={6}>
            <Center
              className={cn(
                paymentMethod === 1
                  ? "bg-primary text-white"
                  : "bg-grey text-black",
                "flex flex-col p-8 gap-4 rounded-lg"
              )}
              onClick={() => setPaymentMethod(1)}
            >
              <Icon
                variants="creditcard"
                className={`h-24 w-24 ${
                  paymentMethod === 1
                    ? "fill-white text-white"
                    : "fill-dark text-dark"
                }`}
              />
              <div>{t("payWithCash")}</div>
            </Center>
          </Col> */}
          </Row>
        </div>
      </div>
    </div>
  );
};

const KioskBuzzer = ({
  buzzer,
  setBuzzer,
}: {
  buzzer: string;
  setBuzzer: (value: string) => void;
}) => {
  const t = useCommon();

  const handleChange = (value: string) => {
    setBuzzer(value);
  };

  return (
    <div>
      <div className="text-center text-[5rem]" style={{ fontWeight: "900" }}>
        {t("buzzerNumber")}
      </div>
      <div className="text-center" style={{ fontWeight: "100" }}>
        {t("buzzerNumberDescription")}
      </div>

      <Center my="10rem">
        <Center flexDirection={"column"} gap="2rem">
          <input
            placeholder={t("buzzerNumber")}
            value={buzzer}
            className="text-center text-[3rem] rounded-4xl p-[2rem_4rem] bg-grey border-[none]"
            style={{ fontWeight: "900" }}
          />
          <div className="my-[1rem]">
            <Numpad onChange={handleChange} />
          </div>
        </Center>
      </Center>
    </div>
  );
};

const KioskUpSelling = () => {
  const { data } = useKioskCategory("3", "15872", false);
  const t = useCommon();
  return (
    <div>
      <div className="text-center text-[5rem]" style={{ fontWeight: "900" }}>
        {t("makeItComplete")}
      </div>
      <div className="text-center" style={{ fontWeight: "100" }}>
        {t("makeItCompleteDescription")}
      </div>

      <div>
        {data && (
          <Row>
            {data?.sub_content?.slice(0, 3).map((product) => (
              <Col
                style={{ marginBottom: "2rem" }}
                md={6}
                lg={4}
                key={product.item_id}
              >
                <KioskProduct
                  parentId={parseInt("15872")}
                  // @ts-ignore
                  product={product}
                />
              </Col>
            ))}
          </Row>
        )}
      </div>
    </div>
  );
};

const KioskCart = ({
  isPending = false,
  showDiscount,
  discount,
  setDiscount,
  setShowDiscount,
}: any) => {
  const showDiscountOption = false;
  const { totalDiscount } = useCartValidatorValuesHook();
  const { setDiscountCode, discountCode } = useDiscountCodeHook();
  const [cart] = useCart();
  const cartTotal = useCartTotal();
  const t = useCommon();
  const setModal = useSetModal();

  useEffect(() => {
    if (cart.length === 0) {
      return setModal("");
    }
  }, [cart]);

  const handleDiscountClick = () => {
    if (discountCode.code) {
      setDiscount("");
      setDiscountCode({ code: "", isApplied: false });
      return;
    }
    setShowDiscount(true);
  };

  const hasDiscount = totalDiscount < 0 && discountCode.code !== "";

  if (showDiscount) {
    return (
      <div>
        <div className="text-center text-[5rem]" style={{ fontWeight: "900" }}>
          {t("posOrders.discountCode")}
        </div>
        <div
          className="text-[2.75rem] text-center"
          style={{ fontWeight: "100" }}
        >
          {t("posOrders.enterDiscountCode")}
        </div>
        <Center my="4rem" justifyContent="center">
          <Center width={"100%"} flexDirection={"column"} gap="2rem">
            <input
              placeholder={t("posOrders.discountCode")}
              value={discount}
              className="text-center text-[3rem] rounded-4xl p-[2rem_4rem] bg-grey border-[none]"
              style={{ fontWeight: "900" }}
            />
            <div className="w-[75%]">
              <QwertyKeyboard
                value={discount}
                setValue={setDiscount}
                onEnter={(v) => setDiscountCode({ code: v, isApplied: true })}
                onChange={(v) => setDiscount(v)}
                isPending={isPending}
              />
            </div>
          </Center>
        </Center>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div
        className="shrink-0 text-center text-[5rem]"
        style={{ fontWeight: "900" }}
      >
        {t("yourOrder")}
      </div>
      <div className="kiosk-scroll-visible mt-[2rem] min-h-0 flex-1 overflow-y-auto overscroll-contain pr-4">
        {cart.map((item, i) => (
          <KioskCartItem
            cartItem={item}
            key={`${item.item_id}-${i}`}
            index={i}
          />
        ))}
      </div>
      {showDiscountOption && (
        <div className="my-[2rem] shrink-0">
          <div className="flex">
            <div className="text-btn-tertiary" style={{ fontWeight: "900" }}>
              {t("posOrders.discountCode")}
            </div>

            <div
              onClick={handleDiscountClick}
              className="ml-auto flex cursor-pointer gap-4"
            >
              <div
                className="text-primary text-btn-tertiary"
                style={{ fontWeight: "900" }}
              >
                {t(
                  discountCode.code
                    ? discountCode.code
                    : "doyouHaveDiscountCode"
                )}
              </div>
              {discountCode.code && (
                <div>
                  <Icon
                    className="h-6 w-6 fill-error text-error"
                    variants="times"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {hasDiscount && (
        <div className="my-[2rem] shrink-0">
          <div className="flex">
            <div className="text-btn-tertiary" style={{ fontWeight: "900" }}>
              {t("posOrders.discount")}
            </div>
            <div className="ml-auto flex cursor-pointer gap-4">
              <div
                className="text-primary text-btn-tertiary"
                style={{ fontWeight: "900" }}
              >
                {formatPrice(totalDiscount)}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="my-[2rem] shrink-0">
        <div className="flex">
          <div className="text-btn-tertiary" style={{ fontWeight: "900" }}>
            {t("total")}
          </div>
          <div
            className="ml-auto text-btn-tertiary"
            style={{ fontWeight: "900" }}
          >
            {formatPrice(cartTotal + (hasDiscount ? totalDiscount : 0))}
          </div>
        </div>
      </div>
    </div>
  );
};

const SUCCESS_SLIDE_INDEX = 2;
const SUCCESS_SCREEN_MS = 5000;

const KioskPaymentSuccess = () => {
  const { t } = useTranslation("", { keyPrefix: "kioskPage" });

  return (
    <Center className="flex min-h-160 flex-col gap-12 py-16 text-center">
      <Icon variants="check" className="h-32 w-32 fill-primary text-primary" />
      <h2 className="text-[5rem] font-black">{t("orderSuccess")}</h2>
      <p className="max-w-7xl text-[3.5rem]">{t("takeYourReceipt")}</p>
    </Center>
  );
};

const KioskCheckout = ({ onExit, ...props }: TableModalProps) => {
  const { data: kioskSettingsQuery } = useKioskSettingsQuery();
  const { data: posUserSettings } = usePosUserSettingsQuery();
  // The terminal chosen for this account (KioskExit), like the POS uses.
  const posTerminal =
    posUserSettings?.default_pos_terminal_id ??
    kioskSettingsQuery?.pos_terminal_id ??
    "";
  const [{ id }] = useModalMeta();
  const [order, setOrder] = useState<OrderV2 | null>(null);
  console.log(order);
  const orderId = order?.order_information?.item_id;
  const { data } = useOrderQuery({
    id: orderId ? `${orderId}` : undefined,
    // Poll faster while waiting for terminal payment (POS uses socket only).
    refetchInterval: orderId ? 5000 : undefined,
    retry: 12,
  });
  const resetKiosk = useKioskReset();
  const printReceipt = usePrintReceipt();
  const [discount, setDiscount] = useState("");
  const [showDiscount, setShowDiscount] = useState(false);
  const [paymentSuccessOrderId, setPaymentSuccessOrderId] = useState<
    number | null
  >(null);
  const hasHandledSuccess = useRef(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const t = useCommon();
  const { i18n } = useTranslation("");
  const history = useHistory();
  const [{ priorityModal }] = usePriorityModal();
  const setPriorityModal = useSetPriorityModal();
  const [cart] = useCart();
  const branchId = useBranchId();
  const { mutateAsync, isPending: isSubmitPending } = usePosOrderPost();
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [buzzer, setBuzzer] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(22);

  const runAfterPost = useCallback(() => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    hasHandledSuccess.current = false;
    setPaymentSuccessOrderId(null);
    setOrder(null);
    setDiscount("");
    resetKiosk();
    setPriorityModal("", {});
    i18n.changeLanguage("nl");
    return history.push("/kiosk");
  }, [history, i18n, resetKiosk, setPriorityModal]);

  const handlePaymentSuccess = useCallback(
    (paidOrder: OrderV2) => {
      if (hasHandledSuccess.current) return;
      hasHandledSuccess.current = true;

      setPaymentSuccessOrderId(paidOrder.order_information.item_id);
      setPriorityModal("", {});
      swiperRef.current?.slideTo(SUCCESS_SLIDE_INDEX);
      printReceipt(
        paidOrder.order_information.item_id,
        paidOrder.order_information.branch_id,
        0
      );
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(runAfterPost, SUCCESS_SCREEN_MS);
    },
    [runAfterPost, setPriorityModal]
  );

  const { isPending } = useCartValidatorHook(
    true,
    () => {
      setShowDiscount(false);
      toast.success(t("discountSuccess"));
    },
    () => {
      toast.error(t("invalidDiscountCode"));
      setDiscount("");
    }
  );

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!order || !data || paymentSuccessOrderId) return;
    if (
      data.order.order_information.order_payment_status ===
      PaymentStatusEnum.PAID
    ) {
      handlePaymentSuccess(data.order);
    }
  }, [order, data, paymentSuccessOrderId, handlePaymentSuccess]);

  // after 1 min if the order is still not paid, we reset the kiosk and go back to the start
  useEffect(() => {
    if (!orderId || paymentSuccessOrderId) return;

    const timer = setTimeout(() => {
      toast.error(t("paymentTimeout"));
      runAfterPost();
    }, 60000);

    return () => clearTimeout(timer);
  }, [orderId, paymentSuccessOrderId, runAfterPost, t]);

  const increaseStep = () => {
    swiperRef?.current?.slideNext();
  };

  const decreaseStep = () => {
    swiperRef?.current?.slidePrev();
  };

  const onPrevClick = () => {
    if (showDiscount) {
      return setShowDiscount(false);
    }
    if (activeIndex === 0) {
      return onExit();
    }
    decreaseStep();
  };

  const onSubmit = () => {
    mutateAsync({
      order_user: {},
      order_information: {
        pos_terminal:
          paymentMethod === PaymentMethodEnum.POS_TERMINAL ? posTerminal : "",
        delivery_cost: "0",
        delivery_method_id: parseInt(id),
        payment_method_id: paymentMethod,
        source: Sources.KIOSK,
        branch_id: branchId,
        loyalty_ledger_points: 0,
        hand_over_is_asap: true,
        tip: 0,
      },
      cart: cart.map((item) => {
        const isCustomCartItem = item.item_id < 0;
        return {
          ...item,
          allergen: undefined,
          item_type: undefined,
          barcode: undefined,
          code: undefined,
          description: undefined,
          vat_enum: undefined,
          status_enum: undefined,
          color: undefined,
          text_color: undefined,
          image_cloudinary_id: undefined,
          price_per_target_value: undefined,
          menu_item_id: isCustomCartItem ? undefined : item.item_id,
          menu_item_price: isCustomCartItem ? item.price : undefined,
          sub_content: item?.sub_content?.map((sub) => ({
            ...sub,
            menu_item_id: sub.item_id,
            item_id: undefined,
            image_cloudinary_id: undefined,
            price_per_target_value: undefined,
          })),
          price: undefined,
          item_id: undefined,
          vat_percentage: 9,
        };
      }),
    })
      .then((createdOrder: OrderV2) => {
        console.log(createdOrder);
        if (
          createdOrder.order_information.payment_method_id ===
          PaymentMethodEnum.POS_TERMINAL
        ) {
          setOrder(createdOrder);
          // / Same shape as POS (usePosOrderHook): eventName + callback
          return setPriorityModal("pendingPayment", {
            orderId: createdOrder.order_information.item_id,
            eventName: `paymentSuccess-${createdOrder.order_information.item_id}`,
            callback: () => handlePaymentSuccess(createdOrder),
            onCancel: () => {
              setOrder(null);
            },
          });
        }

        handlePaymentSuccess(createdOrder);
      })
      .catch();
  };

  const onNextClick = () => {
    if (2 === activeIndex + 1) {
      return onSubmit();
    }
    increaseStep();
  };

  const isDisabled =
    (activeIndex === 2 && buzzer.trim() === "") ||
    (activeIndex === 1 && paymentMethod !== 22 && paymentMethod !== 1);

  const isSuccessScreen = paymentSuccessOrderId !== null;

  return (
    <Modal
      width={"95%"}
      onExit={
        priorityModal === "pendingPayment" || isSuccessScreen
          ? undefined
          : onExit
      }
      closeOnClickOutside={!isSuccessScreen}
      className="flex h-[80vh] flex-col"
      contentClassName="flex min-h-0 flex-1 flex-col"
      {...props}
    >
      {isSuccessScreen ? (
        <div className="flex flex-1 items-center justify-center">
          <KioskPaymentSuccess />
        </div>
      ) : (
        <Swiper
          className="min-h-0 w-full flex-1"
          allowTouchMove={false}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          slidesPerView={1}
        >
          <SwiperSlide className="kiosk-scroll-hidden overflow-y-auto">
            <KioskCart
              discount={discount}
              setDiscount={setDiscount}
              isPending={isPending}
              showDiscount={showDiscount}
              setShowDiscount={setShowDiscount}
            />
          </SwiperSlide>
          {/* <SwiperSlide>
          <KioskUpSelling />
        </SwiperSlide>
        <SwiperSlide>
          <KioskBuzzer buzzer={buzzer} setBuzzer={setBuzzer} />
        </SwiperSlide> */}

          <SwiperSlide>
            <KioskPayments
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
            />
          </SwiperSlide>

          <SwiperSlide>
            <KioskPaymentSuccess />
          </SwiperSlide>
        </Swiper>
      )}
      {!isSuccessScreen && (
        <div className="flex shrink-0 gap-4 pt-8">
          <div className="w-full">
            <Button onClick={onPrevClick} variant="secondary" size="large">
              {t("goBack")}
            </Button>
          </div>
          <div className="w-full">
            {!showDiscount && (
              <Button
                isPending={isSubmitPending}
                isDisabled={isDisabled}
                onClick={onNextClick}
                size="large"
              >
                {t(activeIndex === 1 ? "pay" : "next")}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default KioskCheckout;
